import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api, consulta } from '../api/cliente';
import type { Pagina } from '../api/cliente';
import type { InmuebleResumen, Sede } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import {
  Aviso,
  Cargando,
  Encabezado,
  Etiqueta,
  Paginador,
  Seleccion,
  Tarjeta,
  Vacio,
  fecha,
  pesos,
} from '../ui/piezas';

const ESTADOS = ['BORRADOR', 'EN_REVISION', 'PUBLICADO', 'PAUSADO', 'ARCHIVADO'];

export default function Inmuebles() {
  const { puede } = useSesion();
  const [sede, setSede] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(0);

  const sedes = useQuery({
    queryKey: ['sedes'],
    queryFn: () => api<Sede[]>('/admin/sedes'),
  });

  const listado = useQuery({
    queryKey: ['inmuebles', sede, pagina],
    queryFn: () =>
      api<Pagina<InmuebleResumen>>(`/admin/inmuebles${consulta({ sede, page: pagina, size: 25 })}`),
  });

  // El estado de publicación no es filtro de la API: se filtra aquí, sobre la
  // página que ya llegó. Es honesto decir que solo acota lo visible.
  const filas = (listado.data?.content ?? []).filter(
    (i) => !estado || i.estadoPublicacion === estado,
  );

  return (
    <>
      <Encabezado
        titulo="Inmuebles"
        nota="El inventario completo. El código lo asigna la sede y no cambia nunca."
      >
        {puede('inmueble:crear') && (
          <Link to="/inmuebles/nuevo">
            <button>Cargar inmueble</button>
          </Link>
        )}
      </Encabezado>

      <Aviso error={listado.error} />

      <Tarjeta>
        <div className="rejilla" style={{ marginBottom: 16 }}>
          <Seleccion
            etiqueta="Oficina"
            valor={sede}
            alCambiar={(v) => {
              setSede(v);
              setPagina(0);
            }}
            vacio="Todas"
            opciones={(sedes.data ?? []).map((s) => ({ valor: s.id, texto: s.nombre }))}
          />
          <Seleccion
            etiqueta="Estado de publicación"
            ayuda="Acota lo que se ve en esta página"
            valor={estado}
            alCambiar={setEstado}
            vacio="Todos"
            opciones={ESTADOS.map((e) => ({ valor: e, texto: e.replaceAll('_', ' ').toLowerCase() }))}
          />
        </div>

        {listado.isPending ? (
          <Cargando />
        ) : filas.length === 0 ? (
          <Vacio>
            No hay inmuebles con esos filtros. {puede('inmueble:crear') && 'Carga el primero.'}
          </Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Inmueble</th>
                  <th>Tipo</th>
                  <th>Ubicación</th>
                  <th className="numero">Precio</th>
                  <th>Publicación</th>
                  <th>Comercial</th>
                  <th>Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link to={`/inmuebles/${i.id}`}>{i.codigo}</Link>
                    </td>
                    <td>
                      <div>{i.titulo}</div>
                      {i.unidad && <small style={{ opacity: 0.7 }}>{i.unidad}</small>}
                    </td>
                    <td>{i.tipo}</td>
                    <td>
                      {i.ciudad}
                      {i.zona ? ` · ${i.zona}` : ''}
                    </td>
                    <td className="numero">
                      {i.operaciones.length === 0
                        ? '—'
                        : i.operaciones.map((o) => (
                            <div key={o.tipo}>
                              <small style={{ opacity: 0.7 }}>{o.tipo.toLowerCase()}</small>{' '}
                              {o.precioPublico ? pesos(o.precio) : 'reservado'}
                            </div>
                          ))}
                    </td>
                    <td>
                      <Etiqueta>{i.estadoPublicacion}</Etiqueta>
                    </td>
                    <td>
                      <Etiqueta>{i.estadoComercial}</Etiqueta>
                    </td>
                    <td>{fecha(i.actualizadoEn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginador
          pagina={listado.data?.number ?? 0}
          total={listado.data?.totalElements ?? 0}
          paginas={listado.data?.totalPages ?? 0}
          alCambiar={setPagina}
        />
      </Tarjeta>
    </>
  );
}
